/**
 * 네트워크 상태 모니터링 시스템
 * - 실시간 네트워크 품질 감지 (Ping 기반)
 * - Navigator.connection API가 부정확하므로 실제 Ping 테스트 사용
 * - 30초마다 자동 속도 측정
 * - 온라인/오프라인 이벤트 관리
 */

import { measureNetworkSpeed, getSpeedText } from './speedTest';

class NetworkMonitor {
  constructor() {
    this.listeners = new Set();
    this.networkState = {
      isOnline: navigator.onLine,
      actualSpeed: 'unknown',     // 🆕 실제 측정된 속도 (fast/medium/slow/offline)
      effectiveType: 'unknown',   // Navigator.connection API (참고용)
      downlink: 'unknown',
      rtt: 'unknown',
      isSlowConnection: false,
      shouldUseOfflineMode: false,
      lastUpdated: new Date()
    };

    this.initializeMonitoring();
  }

  /**
   * 네트워크 모니터링 초기화
   */
  initializeMonitoring() {
    // Navigator.connection API 지원 체크
    this.connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;

    // 초기 네트워크 상태 업데이트
    this.updateNetworkState();

    // 온라인/오프라인 이벤트 리스너
    window.addEventListener('online', this.handleOnlineStateChange.bind(this));
    window.addEventListener('offline', this.handleOnlineStateChange.bind(this));

    // Navigator.connection API 변화 감지
    if (this.connection) {
      this.connection.addEventListener('change', this.handleConnectionChange.bind(this));
    }

    // 🆕 주기적 네트워크 속도 측정 (30초마다)
    this.periodicCheck = setInterval(async () => {
      await this.updateNetworkState();
    }, 30000);

    // console.log('[NetworkMonitor] 네트워크 모니터링 시작됨 (Ping 기반)');
  }

  /**
   * 네트워크 상태 업데이트 (Ping 기반)
   */
  async updateNetworkState() {
    const wasSlowConnection = this.networkState.isSlowConnection;
    const wasOfflineMode = this.networkState.shouldUseOfflineMode;
    const wasActualSpeed = this.networkState.actualSpeed;

    // 기본 온라인 상태
    this.networkState.isOnline = navigator.onLine;

    // 🆕 실제 Ping 측정
    this.networkState.actualSpeed = await measureNetworkSpeed();

    // Navigator.connection API 정보 (참고용)
    if (this.connection) {
      this.networkState.effectiveType = this.connection.effectiveType || 'unknown';
      this.networkState.downlink = this.connection.downlink || 'unknown';
      this.networkState.rtt = this.connection.rtt || 'unknown';
    }

    // 🆕 Ping 결과로 느린 연결 판단
    this.networkState.isSlowConnection = (
      this.networkState.actualSpeed === 'slow' ||
      this.networkState.actualSpeed === 'medium' ||
      this.networkState.actualSpeed === 'offline'
    );

    // 🆕 오프라인 모드 사용 여부 (fast만 서버 저장)
    this.networkState.shouldUseOfflineMode = (
      !this.networkState.isOnline ||
      this.networkState.actualSpeed !== 'fast'
    );

    this.networkState.lastUpdated = new Date();

    // 상태 변화시 리스너들에게 알림
    const stateChanged = (
      wasSlowConnection !== this.networkState.isSlowConnection ||
      wasOfflineMode !== this.networkState.shouldUseOfflineMode ||
      wasActualSpeed !== this.networkState.actualSpeed
    );

    if (stateChanged) {
      this.notifyListeners();
      this.logStateChange();
    }
  }

  /**
   * 느린 연결 감지 (더 이상 사용하지 않음, Ping 기반으로 대체)
   * @deprecated
   */
  detectSlowConnection() {
    // Ping 기반으로 판단하므로 이 메서드는 사용하지 않음
    return this.networkState.isSlowConnection;
  }

  /**
   * 오프라인 모드 사용 여부 결정 (더 이상 사용하지 않음, Ping 기반으로 대체)
   * @deprecated
   */
  shouldUseOfflineMode() {
    // Ping 기반으로 판단하므로 이 메서드는 사용하지 않음
    return this.networkState.shouldUseOfflineMode;
  }

  /**
   * 온라인/오프라인 상태 변화 핸들러
   */
  async handleOnlineStateChange() {
    // console.log('[NetworkMonitor] 온라인 상태 변화:', navigator.onLine ? '온라인' : '오프라인');
    await this.updateNetworkState();
  }

  /**
   * Navigator.connection 변화 핸들러
   */
  async handleConnectionChange() {
    // console.log('[NetworkMonitor] 연결 상태 변화 감지');
    await this.updateNetworkState();
  }

  /**
   * 상태 변화 로깅
   */
  logStateChange() {
    const state = this.networkState;
    // console.log('[NetworkMonitor] 네트워크 상태 변화:', {
    //   isOnline: state.isOnline,
    //   actualSpeed: state.actualSpeed,           // 🆕 실제 Ping 속도
    //   effectiveType: state.effectiveType,       // 참고용
    //   isSlowConnection: state.isSlowConnection,
    //   shouldUseOfflineMode: state.shouldUseOfflineMode,
    //   downlink: state.downlink,
    //   rtt: state.rtt
    // });
  }

  /**
   * 네트워크 상태 변화 리스너 등록
   */
  addListener(callback) {
    this.listeners.add(callback);

    // 등록과 동시에 현재 상태 전달
    callback(this.networkState);
  }

  /**
   * 네트워크 상태 변화 리스너 제거
   */
  removeListener(callback) {
    this.listeners.delete(callback);
  }

  /**
   * 모든 리스너들에게 상태 변화 알림
   */
  notifyListeners() {
    this.listeners.forEach(callback => {
      try {
        callback(this.networkState);
      } catch (error) {
        // console.error('[NetworkMonitor] 리스너 실행 오류:', error);
      }
    });
  }

  /**
   * 현재 네트워크 상태 반환
   */
  getNetworkState() {
    return { ...this.networkState };
  }

  /**
   * 네트워크 품질 텍스트 반환 (🆕 Ping 기반)
   */
  getNetworkQualityText() {
    const state = this.networkState;

    if (!state.isOnline) {
      return '오프라인';
    }

    // 🆕 실제 Ping 속도로 판단
    return getSpeedText(state.actualSpeed);
  }

  /**
   * 모니터링 중지
   */
  destroy() {
    window.removeEventListener('online', this.handleOnlineStateChange);
    window.removeEventListener('offline', this.handleOnlineStateChange);

    if (this.connection) {
      this.connection.removeEventListener('change', this.handleConnectionChange);
    }

    if (this.periodicCheck) {
      clearInterval(this.periodicCheck);
    }

    this.listeners.clear();
    // console.log('[NetworkMonitor] 네트워크 모니터링 중지됨');
  }
}

// 싱글톤 인스턴스
const networkMonitor = new NetworkMonitor();

// 편의 함수들
export const getNetworkState = () => networkMonitor.getNetworkState();
export const isOnline = () => networkMonitor.getNetworkState().isOnline;
export const isSlowConnection = () => networkMonitor.getNetworkState().isSlowConnection;
export const shouldUseOfflineMode = () => networkMonitor.getNetworkState().shouldUseOfflineMode;
export const getNetworkQualityText = () => networkMonitor.getNetworkQualityText();
export const addNetworkListener = (callback) => networkMonitor.addListener(callback);
export const removeNetworkListener = (callback) => networkMonitor.removeListener(callback);

export default networkMonitor;